import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { UserObject } from "../models/User";
import { CultureObject } from "../models/Culture";
import { PropostaProviders } from "../components/Details/types";
import useModel from "../hooks/useModel";
import { modelFilters } from "../filters";
import { ModelNames } from "../models";
import { useLocation, useNavigate } from "react-router-dom";
import { TModelFilters } from "../filters/deprecated.types";
import { UserRoles } from "../graphql/__GENERATED__/types";

interface CommonFilterContextProviderProps {
  children: ReactNode;
}

export interface CommonFilterOption {
  value: string;
  label: string;
}

export interface CommonFilterStore {
  partnersList: CommonFilterOption[];
  selectedPartner: string;
  handleSelectPartner: (value: string) => void;
  responsablesList: CommonFilterOption[];
  selectedResponsable: string;
  handleSelectResponsable: (value: string) => void;
  culturesList: CommonFilterOption[];
  selectedCulture: string;
  handleSelectCulture: (value: string) => void;
  providersList: CommonFilterOption[];
  selectedProvider: string;
  handleSelectProvider: (value: string) => void;
  statusList: CommonFilterOption[];
  selectedStatus: string;
  handleSelectStatus: (value: string) => void;
  statusOptionsList: CommonFilterOption[][];
  selectedStatusList: string[];
  handleSelectNestedStatus: (value: string, index: number) => void;
  searchTerm: string;
  handleSearchTermChange: (value: string) => void;
  modelName: ModelNames | null;
  setModelFilter: (
    modelName: ModelNames,
    queryParams: string[],
    initialState: (string | null)[]
  ) => void;
  setQueryParams: (params: string[]) => void;
}

const allPartnersOption: CommonFilterOption = {
  value: "all",
  label: "Todos",
};
const allResponsiblesOption: CommonFilterOption = {
  value: "all",
  label: "Todos",
};
const allCulturesOption: CommonFilterOption = {
  value: "all",
  label: "Todos",
};
const allProvidersOption: CommonFilterOption = {
  value: "all",
  label: "Todos",
};

const initialState: CommonFilterStore = {
  partnersList: [allPartnersOption],
  selectedPartner: allPartnersOption.value,
  handleSelectPartner: () => {},
  responsablesList: [allResponsiblesOption],
  selectedResponsable: allResponsiblesOption.value,
  handleSelectResponsable: () => {},
  culturesList: [allCulturesOption],
  selectedCulture: allCulturesOption.value,
  handleSelectCulture: () => {},
  providersList: [allProvidersOption],
  selectedProvider: allProvidersOption.value,
  handleSelectProvider: () => {},
  statusList: [],
  selectedStatus: "",
  handleSelectStatus: () => {},
  statusOptionsList: [],
  selectedStatusList: [],
  handleSelectNestedStatus: () => {},
  searchTerm: "",
  handleSearchTermChange: () => {},
  modelName: null,
  setModelFilter: () => {},
  setQueryParams: () => {},
};

const CommonFilterContext = createContext<CommonFilterStore>(initialState);

export const CommonFilterContextProvider: FC<
  CommonFilterContextProviderProps
> = ({ children }) => {
  const usersModel = useModel<UserObject>("users");
  const culturesModel = useModel<CultureObject>("culturas");
  const location = useLocation();

  // Partners
  const [partnersList, setPartnersList] = useState<CommonFilterOption[]>(
    initialState.partnersList
  );
  const [selectedPartner, setSelectedPartner] = useState<string>(
    initialState.selectedPartner
  );

  const loadPartners = useCallback(async () => {
    const res = await usersModel.search({ role: UserRoles.Consultant });
    setPartnersList(
      partnersList.concat(
        res.map((partner) => ({ value: partner._id, label: partner.nome! }))
      )
    );
  }, [usersModel]);

  // Responsables
  const [responsablesList, setResponsablesList] = useState<
    CommonFilterOption[]
  >(initialState.responsablesList);
  const [selectedResponsable, setSelectedResponsable] = useState<string>(
    initialState.selectedResponsable
  );
  const loadResponsibles = useCallback(async () => {
    const res = await usersModel.search({ role: UserRoles.Admin });
    setResponsablesList(
      responsablesList.concat(
        res.map((admin) => ({ value: admin._id, label: admin.nome! }))
      )
    );
  }, [usersModel]);
  // Cultures
  const [culturesList, setCulturesList] = useState<CommonFilterOption[]>(
    initialState.culturesList
  );
  const [selectedCulture, setSelectedCulture] = useState<string>(
    initialState.selectedCulture
  );
  const loadCultures = useCallback(async () => {
    const res = await culturesModel.search({});
    setCulturesList(
      culturesList.concat(
        res.map((culture) => ({
          value: culture._id,
          label: culture.name!,
        }))
      )
    );
  }, [culturesModel]);

  // Providers
  const [providersList, setProvidersList] = useState<CommonFilterOption[]>(
    initialState.providersList
  );
  const [selectedProvider, setSelectedProvider] = useState<string>(
    initialState.selectedProvider
  );
  const loadProviders = useCallback(async () => {
    setProvidersList(
      providersList.concat(
        Object.values(PropostaProviders).map((provider) => ({
          value: provider,
          label: provider.toUpperCase(),
        }))
      )
    );
  }, [PropostaProviders]);

  useEffect(() => {
    loadPartners();
    loadResponsibles();
    loadCultures();
    loadProviders();
  }, []);

  // Status
  const [modelName, setModelName] = useState<ModelNames | null>(null);
  const [statusList, setStatusList] = useState<CommonFilterOption[]>(
    initialState.statusList
  );
  const [selectedStatus, setSelectedStatus] = useState(
    initialState.selectedStatus
  );
  useEffect(() => {
    if (modelName) {
      const modelStatusOptions =
        modelFilters[modelName as unknown as keyof typeof modelFilters];
      const newStatusList = Object.entries(modelStatusOptions).map(
        ([key, value]) => ({ value: key, label: value.name })
      );
      setStatusList(newStatusList);
      if (!selectedStatus) {
        setSelectedStatus(newStatusList[0].value);
      }
    }
  }, [modelName, location]);

  // Status (with nested)
  const [statusOptionsList, setStatusOptionsList] = useState<
    CommonFilterOption[][]
  >(initialState.statusOptionsList);
  const [selectedStatusList, setSelectedStatusList] = useState<string[]>(
    initialState.selectedStatusList
  );

  const getNewStatusOptionsList = (
    newSelectedStatusList: string[],
    modelFilter: TModelFilters
  ): CommonFilterOption[][] => {
    const filtersList = [modelFilter];
    newSelectedStatusList.forEach((value, index) => {
      const selectedFilter = filtersList[index]?.find(
        (filter) => filter.value === value
      );
      if (selectedFilter && selectedFilter.nestedFilters) {
        filtersList.push(selectedFilter.nestedFilters);
      }
    });
    return filtersList.map((filters) =>
      filters.map(({ name, value }) => ({ label: name, value }))
    );
  };

  const handleSelectNestedStatus = (value: string, index: number) => {
    const newSelectedStatusList = selectedStatusList.slice(0, index);
    newSelectedStatusList.push(value);
    setSelectedStatusList(newSelectedStatusList);
    const newStatusOptionsList = getNewStatusOptionsList(
      newSelectedStatusList,
      modelFilters[modelName as unknown as keyof typeof modelFilters]
    );
    setStatusOptionsList(newStatusOptionsList);
  };

  // Search
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);

  const [queryParams, setQueryParams] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams();
    selectedStatusList.forEach((value, index) => {
      searchParams.set(queryParams[index], value);
    });
    navigate(`${location.pathname}?${searchParams.toString()}`);
  }, [queryParams, selectedStatusList]);

  const setModelFilter = (
    model: ModelNames,
    queryParams: string[],
    initialState: (string | null)[]
  ) => {
    setModelName(model);
    setQueryParams(queryParams);
    const initialSelectedStatusList = [];
    for (let value of initialState) {
      if (value) {
        initialSelectedStatusList.push(value);
      } else {
        break;
      }
    }
    if (!initialSelectedStatusList.length) {
      initialSelectedStatusList.push(
        modelFilters[model as unknown as keyof typeof modelFilters][0].value
      );
    }
    setSelectedStatusList(initialSelectedStatusList);
    setStatusOptionsList(
      getNewStatusOptionsList(
        initialSelectedStatusList,
        modelFilters[model as unknown as keyof typeof modelFilters]
      )
    );
  };

  return (
    <CommonFilterContext.Provider
      value={{
        partnersList,
        selectedPartner,
        handleSelectPartner: setSelectedPartner,
        responsablesList,
        selectedResponsable,
        handleSelectResponsable: setSelectedResponsable,
        culturesList,
        selectedCulture,
        handleSelectCulture: setSelectedCulture,
        providersList,
        selectedProvider,
        handleSelectProvider: setSelectedProvider,
        statusList,
        selectedStatus,
        handleSelectStatus: setSelectedStatus,
        statusOptionsList,
        selectedStatusList,
        handleSelectNestedStatus,
        searchTerm,
        handleSearchTermChange: setSearchTerm,
        modelName,
        setModelFilter,
        setQueryParams,
      }}
    >
      {children}
    </CommonFilterContext.Provider>
  );
};

export default CommonFilterContext;
